import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Plus, 
  Edit, 
  Loader2, 
  Briefcase,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceTypesPage = () => {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    is_active: true,
    sort_order: 99
  });

  useEffect(() => {
    fetchServiceTypes();
  }, []);

  const fetchServiceTypes = async () => {
    try {
      const response = await axios.get(`${API}/admin/service-types?include_inactive=true`);
      setServiceTypes(response.data);
    } catch (error) {
      toast.error('Failed to load service types');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setFormData({
        id: type.id,
        name: type.name,
        description: type.description || '',
        is_active: type.is_active,
        sort_order: type.sort_order || 99
      });
    } else {
      setEditingType(null);
      setFormData({
        id: '',
        name: '',
        description: '',
        is_active: true,
        sort_order: 99
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingType) {
        await axios.put(`${API}/admin/service-types/${editingType.id}`, {
          name: formData.name,
          description: formData.description,
          sort_order: formData.sort_order
        });
        toast.success('Service type updated successfully');
      } else {
        // Generate ID from name if not provided
        const typeId = formData.id || formData.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        await axios.post(`${API}/admin/service-types`, {
          ...formData,
          id: typeId
        });
        toast.success('Service type created successfully');
      }
      setShowDialog(false);
      fetchServiceTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service type');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (type) => {
    try {
      await axios.post(`${API}/admin/service-types/${type.id}/toggle-status`);
      toast.success(`Service type ${type.is_active ? 'deactivated' : 'activated'}`);
      fetchServiceTypes();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="service-types-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Briefcase className="w-8 h-8 text-primary" />
            Service Types
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage case service types for case management
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Add Service Type
        </Button>
      </div>

      {/* Info Alert */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900 dark:text-blue-100">Configurable without code changes</p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Service types can be added, edited, or deactivated here. Changes take effect immediately.
                Service types drive reporting, workflow triggers, and compliance tracking.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service Types List */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle>All Service Types</CardTitle>
          <CardDescription>
            {serviceTypes.filter(t => t.is_active).length} active, {serviceTypes.filter(t => !t.is_active).length} inactive
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {serviceTypes.map((type) => (
              <div 
                key={type.id}
                className={`p-4 rounded-lg border ${type.is_active ? 'border-border' : 'border-dashed border-muted-foreground/30 bg-muted/30'}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{type.name}</h3>
                      {type.is_active ? (
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{type.description}</p>
                    <p className="text-xs text-muted-foreground mt-2 font-mono">ID: {type.id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleOpenDialog(type)}>
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant={type.is_active ? "destructive" : "default"} 
                      size="sm"
                      onClick={() => handleToggleStatus(type)}
                    >
                      {type.is_active ? (
                        <>
                          <XCircle className="w-4 h-4 mr-1" />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Activate
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Service Type' : 'New Service Type'}</DialogTitle>
            <DialogDescription>
              {editingType 
                ? 'Update the service type details. Changes take effect immediately.'
                : 'Create a new service type. It will be available for case creation immediately.'
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {!editingType && (
                <div className="space-y-2">
                  <Label htmlFor="id">Type ID (optional)</Label>
                  <Input
                    id="id"
                    value={formData.id}
                    onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                    placeholder="auto-generated from name"
                  />
                  <p className="text-xs text-muted-foreground">Leave empty to auto-generate from name</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="name">Display Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Job Search Training"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this service type"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sort_order">Sort Order</Label>
                <Input
                  id="sort_order"
                  type="number"
                  min="1"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 99 }))}
                />
                <p className="text-xs text-muted-foreground">Lower numbers appear first in dropdowns</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingType ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceTypesPage;
